import { BeatSequencer } from '@/story/sequencer';
import { OFFICE_BEAT, OFFICE_CUES, OFFICE_FLAGS } from '@/story/act3/office';
import { OFFICE_SPAWN, isPastOfficeExit } from '@/world/office';
import type { BeatFadeCues } from '@/world/beatFadeDirector';
import type { Framing } from '@/camera/framing';
import type { BeatPartCapabilities, BeatPartModule, BeatPartModuleDefinition } from '@/game/beatPartModule';

export const OFFICE_FADE_CUES: BeatFadeCues = OFFICE_CUES;

/**
 * The opening (D-213, audit row 5): low, from the gap behind the filing
 * cabinet she came in by, over her to him. The cabinet's edge screens the left
 * of the frame; past it, him at the desk with the lamp on the page, his hat on
 * the wall, and the door ajar beyond — the plan's stage A, *"the room, and he
 * is in it"*. Measured spread 9.5 % at 6 s, against 1.9 % for the follow
 * camera from the old spawn (`wayfinding-frames.spec.ts`, red-watched).
 *
 * Held as beat 14's opening is: until she walks, or for `OPENING_HOLD`. The
 * leash is shorter than the hill's because the room is.
 *
 * Portrait (D-246): at 390 × 844 the wide shot kept him and lost her (NDC x
 * 1.09). Re-aimed from the gap, a 76° lens holds both, with him a small figure
 * across the room. So the portrait lens stands behind her on the line to his
 * desk, low, and looks over her: her ears
 * and back a dark shape in the foreground, him in the lamplight, the hat and
 * the door beyond. The cabinet's screen is what it gives up.
 */
export const OFFICE_OPENING_SHOT: Framing = {
  position: { x: 1.90, y: 0.70, z: 1.90 },
  lookAt: { x: -1.30, y: 0.55, z: -1.10 },
  fov: 62,
  portrait: {
    position: { x: 1.85, y: 0.60, z: 1.30 },
    lookAt: { x: -1.35, y: 0.60, z: -0.75 },
    fov: 72,
  },
};
export const OFFICE_OPENING_HOLD = 10;
export const OFFICE_OPENING_LEASH = 0.8;

/** Beat 11 part 1: a room crossing with presentation-only fades and one held shot. */
function createOfficePartModule(capabilities: BeatPartCapabilities): BeatPartModule {
  const story = new BeatSequencer(OFFICE_BEAT);
  const fade = capabilities.createFade(OFFICE_FADE_CUES);
  const her: { x: number; z: number } = { x: OFFICE_SPAWN.x, z: OFFICE_SPAWN.z };
  let wandered = false;
  let disposed = false;

  return {
    story,
    interactables: [],
    get fade(): number { return fade.fade; },
    get promptSuppressed(): boolean { return !disposed && fade.fade > 0.5; },
    fixedStep({ player, raiseFlag }): void {
      her.x = player.x;
      her.z = player.z;
      // Latched: once she has walked, the lens is hers for the rest of the
      // beat, even if she walks back to where she stood.
      if (Math.hypot(her.x - OFFICE_SPAWN.x, her.z - OFFICE_SPAWN.z) > OFFICE_OPENING_LEASH) wandered = true;
      if (isPastOfficeExit(player)) raiseFlag(OFFICE_FLAGS.leftOffice);
    },
    handleCue(cue): void { fade.handle(cue); },
    renderStep(frameDt): void { if (!disposed) fade.update(frameDt); },
    framing(): Framing | null {
      const held = story.stageId === 'crossing' && !wandered && story.debug.stageTime < OFFICE_OPENING_HOLD;
      return held ? OFFICE_OPENING_SHOT : null;
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      fade.dispose();
    },
  };
}

/**
 * The registry owns this declaration; the host injects the DOM-owning fade.
 * That keeps the authored story and exit rule executable in the Node sim lane.
 */
export const OFFICE_PART_MODULE: BeatPartModuleDefinition = {
  beat: 11,
  part: 1,
  room: 'office',
  create: createOfficePartModule,
};
